import { Flexbox, ThemeButton, ThemeDivider } from '@ibinder/ui-common-core';
import { ThemeSideSheet } from '@ibinder/ui-common-dialogs';
import { buttonVariants } from '@ibinder/ui-common-styles';
import { useFeatureToggle } from '@ibinder/ui-common-hooks';
import React, { useEffect } from 'react';
import {
  isFormDataValid,
  isFormValid,
  ViewWorkflowFormFields,
  translate_FormTypeValueEnum_to_MetadataTypeEnum
} from '@ibinder/ui-common-forms';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import {
  FormData,
  ModalContainer,
  ModeType,
  ScrollableContent
} from './transformation';
import {
  getStandardIssueTemplate,
  getStandardWorkflowTemplate
} from './transformation';
import {
  GET_ISSUE_TEMPLATE,
  GET_WORKFLOW_TEMPLATE
} from '../../api/queries/queries_local';
import { useLazyQuery } from '@apollo/client';
import { iBinderHeaderEnum, IssueTemplate } from '@ibinder/ui-common-models';
import styled from 'styled-components';

const StyledModalContainer = styled(ModalContainer)`
  label {
    padding-top: 0.25rem; 
  }
`;


const CreateWorkflowTemplateSideSheet = ({
  onClose,
  labels,
  t,
  createIssueTemplate,
  createWorkflowTemplate,
  updateWorkflowTemplate,
  updateIssueTemplate,
  selectedId,
  mode,
  marketInfo
}) => {
  const listofMarkets = marketInfo?.data?.get || [];
  const marketLists = [
    { Key: "All", Value: labels.all },
    ...listofMarkets.map((market) => ({
      Key: market.country.countryCode,
      Value: market.name,
    })),
  ].sort((a, b) => a.Value.localeCompare(b.Value));
  const [
    getWorkflowTemplate,
    { data: workflowTemplatedata, loading: workflowLoading, error: workflowError },
  ] = useLazyQuery(GET_WORKFLOW_TEMPLATE, {
    fetchPolicy: "no-cache",
    context: {
      [iBinderHeaderEnum.TENANTID]: ''
    },
  });

  const { isEnabled: canUseExpandWorkflowFunctions } = useFeatureToggle(
    'Configcat_ExpandWorkflowFunctions'
  );
  const { isEnabled: expandWorkflowWithFlags } = useFeatureToggle(
    'Configcat_ExpandWorkflowWithFlags'
  );
  const { isEnabled: expandWorkflowWithLocation } = useFeatureToggle(
    'Configcat_ExpandWorkflowWithLocation'
  );
  const { isEnabled: expandWorkflowWithDelegation } = useFeatureToggle(
    'Configcat_ExpandWorkflowWithDelegation'
  );
  const { isEnabled: issuesOnSpecificDrawing } = useFeatureToggle(
    'Configcat_IssuesOnSpecificDrawing'
  );

  const [
    getIssueTemplateData,
    { data: issueTemplateData, loading: issueLoading, error: issueError },
  ] = useLazyQuery(GET_ISSUE_TEMPLATE, {
    fetchPolicy: "no-cache",
    context: {
      [iBinderHeaderEnum.TENANTID]: ''
    },
  });

  // Promise wrappers so we can await
  const fetchWorkflowTemplate = (variables) =>
    new Promise((resolve, reject) => {
      getWorkflowTemplate({
        variables,
        onCompleted: resolve,
        onError: reject,
      });
    });

  const fetchIssueTemplateData = (variables) =>
    new Promise((resolve, reject) => {
      getIssueTemplateData({
        variables,
        onCompleted: resolve,
        onError: reject,
      });
    });

  // Initialize the form with default values
  const methods = useForm<FormData, any, FormData>({
    mode: 'onChange',
    defaultValues: {
      // @ts-ignore
      workFlow: getStandardWorkflowTemplate('', '', labels),
      // @ts-ignore
      issueTemplate: getStandardIssueTemplate('', labels)
    }
  });

  // Move hooks to top level
  const { handleSubmit, reset } = methods;

  // Run when modal opens
  useEffect(() => {
    const runQueries = async () => {
      try {
        const workflowTemplatedata = await fetchWorkflowTemplate({
          id: selectedId,
        });
        const workflowTemplateResult = workflowTemplatedata as { getWorkflowTemplate?: any };
        const issueTemplateData = await fetchIssueTemplateData({
          id: workflowTemplateResult?.getWorkflowTemplate?.issueTemplateId,
        });
        const issueTemplateResult = issueTemplateData as { getIssueTemplate?: IssueTemplate };
        
        const workflowTemplate = workflowTemplateResult?.getWorkflowTemplate;
        const issueTemplate = issueTemplateResult?.getIssueTemplate;

        if(workflowTemplate && issueTemplate){
            console.log("Fetched templates:", { workflowTemplate, issueTemplate });
            if(mode === ModeType.Duplicate){
               reset({
                  workFlow: {...workflowTemplate, name:'', instructions:''},
                  issueTemplate: issueTemplate
                });
                return;
            }
            // Set form with fetched data
            reset({
              workFlow: workflowTemplate,
              issueTemplate: issueTemplate
            });
            return;
        }
      } catch (err) {
        console.error("Error fetching templates:", err);
      }
    };
    if (selectedId) {
      runQueries();
    }
  }, [selectedId, mode, reset]);


  const onSubmitForm = async (data: FormData) => {
    if (!isFormDataValid(data)) {
      return;
    }
    if (mode === ModeType.Duplicate) {
      delete data?.issueTemplate?.id;
      delete data?.workFlow?.id;
    }
    
    data.issueTemplate.flags = data.issueTemplate.flags?.filter(Boolean) ?? [];
    data.issueTemplate.items = data.issueTemplate.items.map((item) => {
      item.itemMetadata = {
        ...item.itemMetadata,
        //@ts-ignore
        type: translate_FormTypeValueEnum_to_MetadataTypeEnum({
          formTypeValue: item.formType.value
        }),
        name: item.formType.formLabel
      };

      return item;
    });
    const stateSettings = data.workFlow.stateSettings;
    if (stateSettings.states && stateSettings.states.length > 0) {
      stateSettings.states = stateSettings.states.map((state, index) => {
        return {
          ...state,
          state: index + 1
        };
      });

      stateSettings.startState = stateSettings.states[0].state;
      stateSettings.endState =
        stateSettings.states[stateSettings.states.length - 1].state;
    }
    // Create workflow using the mutations
    if(mode === ModeType.Edit){
      await updateWorkflowTemplate({
        variables: {
          id: data.workFlow.id,   
          body: { 
            ...data.workFlow,
            name: data.workFlow.name,
          }
        }
      });
      await updateIssueTemplate({
        variables: {
          id: data.issueTemplate.id,   
          body: { 
            ...data.issueTemplate, 
            name: data.workFlow.name 
          }
        }
      });
      onClose();
      return;
    }
    try {
      const issueResult = await createIssueTemplate({
        variables: {
          payload: { ...data.issueTemplate, name: data.workFlow.name }
        }
      });
      onClose();
      const issueId = issueResult?.data?.createIssueTemplate?.id;
      if (!issueId) {
        console.error('Error creating issue template: No ID returned');
      }
      const workflowResult = await createWorkflowTemplate({
        variables: {
          payload: {
            ...data.workFlow,
            issueTemplateId: issueId
          }
        }
      });
    } catch (error) {
      console.error('Error creating workflow:', error);
    }
  };

  const workflowTemplateName = methods.watch("workFlow.name");
  const formIsValid = isFormValid(
    workflowTemplateName,
    methods.watch("workFlow.instructions"),
    methods.watch("issueTemplate"),
    methods.watch("workFlow.market"),
  );

  const loading = workflowLoading || issueLoading;
  const isDefaultTemplate = (name, loading) => 
  !loading && (name ?? "").trim().toLowerCase().includes("default");
  const isDefaultGlobalTemplate = isDefaultTemplate(workflowTemplateName, loading);
  return (
    <FormProvider {...methods}>
      <ThemeSideSheet
        headerText={mode === ModeType.Edit ? workflowTemplateName : labels.createNewTemplate }
        loading={loading}
        open={true}
        onClose={onClose}
        marginTop="4.5rem"
        footer={
          <Flexbox justifyContent={'space-between'}>
            {
              <>
                <ThemeButton
                  buttonType={buttonVariants.Secondary}
                  onClick={onClose}
                >
                  {labels.cancel}
                </ThemeButton>
                <Flexbox>
                  <ThemeButton
                    buttonType={buttonVariants.Primary}
                    onClick={handleSubmit(onSubmitForm)}
                    disabled={!formIsValid}
                  >
                    {mode === ModeType.Edit ? labels.save : labels.create}
                  </ThemeButton>
                </Flexbox>
              </>
            }
          </Flexbox>
        }
      >
        <ScrollableContent>
          <ThemeDivider />
          <StyledModalContainer>
            <ViewWorkflowFormFields
             expandWorkflowWithFlags={expandWorkflowWithFlags}
             expandWorkflowWithLocation={expandWorkflowWithLocation}
             expandWorkflowWithDelegation={expandWorkflowWithDelegation}
             issuesOnSpecificDrawing={issuesOnSpecificDrawing}
             canUseExpandWorkflowFunctions={canUseExpandWorkflowFunctions}
              labels={labels}
              t={t}
              tenantSpecific={!isDefaultGlobalTemplate}
              control={methods.control}
              register={methods.register}
              setValue={methods.setValue}
              watch={methods.watch}
              getValues={methods.getValues}
              useFieldArray={useFieldArray}
              globalWorkflowTemplate={true}
              marketList={marketLists}
            />
          </StyledModalContainer>
        </ScrollableContent>
      </ThemeSideSheet>
    </FormProvider>
  );
};

export default CreateWorkflowTemplateSideSheet;
